import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import toast from 'react-hot-toast';

interface OneTimeKeyNoticeProps {
  name: string;
  secret: string;
  onDismiss: () => void;
}

// Shows a freshly created API key. The server keeps only a hash, so this is
// the one chance to copy it.
const OneTimeKeyNotice: React.FC<OneTimeKeyNoticeProps> = ({ name, secret, onDismiss }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      toast.success('Key copied');
    } catch {
      toast.error('Could not copy — select the key and copy it manually');
    }
  };

  return (
    <div className="rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-4">
      <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200 mb-2">
        New key "{name}" — copy it now, it won't be shown again.
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 break-all select-all text-xs bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800 rounded px-2 py-2 text-slate-800 dark:text-slate-100">
          {secret}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <button type="button" onClick={onDismiss} className="mt-2 text-xs text-emerald-800 dark:text-emerald-300 underline">
        I've saved it — hide
      </button>
    </div>
  );
};

export default OneTimeKeyNotice;
